import React, { useState } from "react";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { useParams } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  CircularProgress,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  IncidentStatus,
  statusColors,
} from "../../../data/FormColumns/Fleet/incidentsTableColumns";
import {
  formatPaymentMethod,
  formatState,
  formatDate,
} from "../../../helpers/formatingFunctions";
import { formatIncidentType } from "../../../helpers/formatingFunctions";
import DownloadButton from "../../../components/Fleet/DownloadButton/DownloadButton";

const fetchIncidentsAndRepairs = async (
  token: string | null | undefined,
  id: string | undefined,
  assetType: string,
) => {
  const endpoint =
    assetType === "truck"
      ? "getTruckIncidentsAndRepairs"
      : "getTrailerIncidentsAndRepairs";
  const { data } = await axios.get(`${apiVariable}/${endpoint}/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const AssetRepairs = ({ assetType }: { assetType: string }) => {
  const { id } = useParams();
  const { token } = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const {
    data: assetIncidentsAndRepairs,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["assetIncidentsAndRepairs"],
    queryFn: () => fetchIncidentsAndRepairs(token, id, assetType),
  });

  const paginatedData = assetIncidentsAndRepairs
    ? assetIncidentsAndRepairs.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      )
    : [];

  const handleChangePage = (
    event: any,
    newPage: React.SetStateAction<number>,
  ) => setPage(newPage);
  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: "auto", mt: 4 }}>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Paper
          elevation={0}
          sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}
        >
          <Typography>Error: {(error as any).message}</Typography>
        </Paper>
      ) : assetIncidentsAndRepairs.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ p: 3, textAlign: "center", bgcolor: "background.paper" }}
        >
          <Typography variant="body1" color="text.secondary">
            No data available.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {paginatedData.map((incident: any, index: any) => (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                "&:before": {
                  display: "none",
                },
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                borderRadius: "8px !important",
                overflow: "hidden",
                "&.Mui-expanded": {
                  margin: "16px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-${incident.id}-content`}
                id={`panel-${incident.id}-header`}
                sx={{
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                >
                  <strong>{`Incident on ${new Date(
                    incident.openDate,
                  ).toLocaleDateString()}`}</strong>
                  {`: ${incident.complaint}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "background.default" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Incident Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Incident Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Payment Method
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Authorized By
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Final Cost
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Shop Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Completed Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Invoice Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Invoice Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{formatState(incident.location)}</TableCell>
                        <TableCell>
                          {formatIncidentType(incident.incidentType)}
                        </TableCell>
                        <TableCell>
                          {incident.incidentStatus !== null ? (
                            <Chip
                              label={
                                statusColors[
                                  incident.incidentStatus as IncidentStatus
                                ].label
                              }
                              color={
                                statusColors[
                                  incident.incidentStatus as IncidentStatus
                                ].color
                              }
                              size="small"
                              sx={{
                                fontWeight: 500,
                                minWidth: "100px",
                              }}
                            />
                          ) : (
                            <Chip
                              label="Not started"
                              color="default"
                              size="small"
                              sx={{
                                fontWeight: 500,
                                minWidth: "100px",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {formatPaymentMethod(incident.paymentMethod)}
                        </TableCell>
                        <TableCell>{incident.authorizedBy}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {"$" + incident.finalCost}
                        </TableCell>
                        <TableCell>{incident.shopName}</TableCell>
                        <TableCell>
                          {incident.completedDate
                            ? new Date(
                                incident.completedDate,
                              ).toLocaleDateString()
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {incident.invoiceDate
                            ? formatDate(incident.invoiceDate)
                            : "N/A"}
                        </TableCell>
                        <TableCell>{incident.invoiceNumber || "N/A"}</TableCell>
                        {incident.invoiceTitle && (
                          <TableCell>
                            <DownloadButton
                              params={{
                                row: {
                                  id: incident.repairId,
                                },
                              }}
                              type="invoice"
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={assetIncidentsAndRepairs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-select": {
                borderRadius: 1,
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  margin: 0,
                },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default AssetRepairs;
